import React from 'react';
import { Grid, Box, IconButton } from '@mui/material';
import SparkdefaultIcon from 'components/SVG/SparkdefaultIcon';
import SparkOptionsModal from 'components/common/SparkOptionsModal';
import { useCurrentUser, useResize } from 'hooks/common';
import { MenuIcon } from 'components/SVG';
import RunSparkModalSideBar from './RunSparkModalSideBar';
import type { CreateFavouriteListFormValues } from 'components/common/Favourite/CreateFavouriteListPopUp';
import type { JoinedSparks } from 'types/Spark/JoinedSpark';
import type {
  LoggedInUserFavouriteListMeta,
  LoggedInUserMeta,
  Rating,
} from 'types/common';
import type { SparkReportFormValues } from 'components/common/Report/SparkReport';
import type { DropdownOption } from 'types/common/DropdownOption';
import type { ReviewFormModalValues } from 'components/common/ReviewFormModal';
import { SparkEllipsisTypography } from 'sharedComponents/UI';
import SparkBadges from 'components/common/SparkBadges';
import { getGropedSparkBadges } from 'helpers/groupSparkBadges';
import { SparkImageLoader } from 'sharedComponents/UI';
import SparkAvatarBackground from 'sharedComponents/UI/SparkAvatarBackground';

type SparkPopupHeaderProps = {
  joinedSpark: JoinedSparks | undefined;
  onCreateList?: (values: CreateFavouriteListFormValues) => void;
  onFavouriteItemClick?: (itemID: number) => void;
  isLoading?: boolean | undefined;
  existingFavouriteList?: LoggedInUserFavouriteListMeta[];
  handleSendReport: (values: SparkReportFormValues) => Promise<void>;
  flagReasonsDropdownOptions: DropdownOption[];
  handleSparkRatingSave: (values: ReviewFormModalValues) => Promise<void>;
  userRating: Rating | undefined;
  allowedToReview: boolean;
  onDelete: () => void;
  onArchive: () => void;
  userPermissions: LoggedInUserMeta['permissions'];
};
const SparkPopupHeader = ({
  joinedSpark,
  handleSendReport,
  existingFavouriteList,
  isLoading,
  onCreateList,
  onFavouriteItemClick,
  flagReasonsDropdownOptions,
  handleSparkRatingSave,
  userRating,
  allowedToReview,
  onArchive,
  onDelete,
  userPermissions,
}: SparkPopupHeaderProps) => {
  const { isMobile } = useResize();
  const { currentUserID } = useCurrentUser();
  const [showSidebar, setShowSidebar] = React.useState(false);
  const toggleOpen = () => {
    setShowSidebar((pre) => !pre);
  };

  const badgesCategoryMap = getGropedSparkBadges(joinedSpark?.meta?.badges);
  return (
    <Grid
      container
      justifyContent='space-between'
      alignItems='center'
      gap={1}
      sx={(theme) => ({
        padding: isMobile ? 2 : '0px 32px',
        paddingTop: 0,
        [theme.breakpoints.down(1000)]: {
          padding: isMobile ? 0 : 2,
        },
      })}
    >
      {isMobile && (
        <Box>
          <IconButton onClick={toggleOpen}>
            <MenuIcon stroke='white' />
          </IconButton>
        </Box>
      )}
      <Grid item>
        <Box
          sx={{
            width: isMobile ? '50px' : '100px',
            height: isMobile ? '50px' : '100px',
            border: '1px solid white',
            borderRadius: '12px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: isMobile ? 0 : -4,
          }}
        >
          {joinedSpark?.attributes.iconURL ? (
            <SparkImageLoader
              width='100%'
              height='100%'
              style={{ borderRadius: '12px' }}
              src={joinedSpark?.attributes.iconURL}
              alt=''
            />
          ) : (
            // <SparkdefaultIcon width={'80px'} height={'80px'} />
            <SparkAvatarBackground
              icon={<SparkdefaultIcon />}
              width='100%'
              height='100%'
            />
          )}
        </Box>
      </Grid>
      <Grid item container flex={1} direction='column' alignSelf='center'>
        <Box display='flex' alignItems='center' gap={1} sx={{ width: '100%' }}>
          <SparkEllipsisTypography
            variant='h5'
            title={joinedSpark?.attributes.nameforHuman || ''}
            sx={{
              WebkitLineClamp: 1,
              wordBreak: 'keep-all',
            }}
          >
            {joinedSpark?.attributes.nameforHuman || '- -'}
          </SparkEllipsisTypography>
        </Box>
        <SparkBadges badges={badgesCategoryMap?.['Creator'] || []} />
      </Grid>
      {!!currentUserID && (
        <>
          {!isMobile && (
            <Grid
              item
              container
              flex={0.3}
              gap={1}
              justifyContent='flex-end'
              flexWrap='nowrap'
            >
              <SparkOptionsModal
                resourceLogoURL={joinedSpark?.attributes.iconURL || ''}
                options={joinedSpark?.meta.options}
                resourceID={joinedSpark?.id}
                resourceAccessLinkToken={
                  joinedSpark?.attributes.accessLinkToken
                }
                privacyTypeId={joinedSpark?.attributes.sparkPrivacyTypeID}
                handleSparkRatingSave={handleSparkRatingSave}
                userRating={userRating}
                allowedToReview={allowedToReview}
                resourceType='spark'
                onFavouriteItemClick={onFavouriteItemClick}
                existingFavouriteList={existingFavouriteList || []}
                onCreateList={onCreateList}
                isLoading={isLoading}
                onSendReport={handleSendReport}
                flagReasonsDropdownOptions={flagReasonsDropdownOptions}
                isResourceFavourite={joinedSpark?.meta.user.isFavourited}
                onDelete={onDelete}
                onArchive={onArchive}
                resourceName={joinedSpark?.attributes?.nameforHuman || '- -'}
                userPermissions={userPermissions}
              />
            </Grid>
          )}
          {showSidebar && (
            <RunSparkModalSideBar open={showSidebar} toggleOpen={toggleOpen}>
              <Grid item container flex={1} gap={1}>
                <SparkOptionsModal
                  resourceLogoURL={joinedSpark?.attributes.iconURL || ''}
                  options={joinedSpark?.meta.options}
                  resourceID={joinedSpark?.id}
                  handleSparkRatingSave={handleSparkRatingSave}
                  userRating={userRating}
                  allowedToReview={allowedToReview}
                  resourceType='spark'
                  onFavouriteItemClick={onFavouriteItemClick}
                  existingFavouriteList={existingFavouriteList || []}
                  onCreateList={onCreateList}
                  isLoading={isLoading}
                  onSendReport={handleSendReport}
                  flagReasonsDropdownOptions={flagReasonsDropdownOptions}
                  isResourceFavourite={joinedSpark?.meta.user.isFavourited}
                  onDelete={onDelete}
                  onArchive={onArchive}
                  resourceName={joinedSpark?.attributes?.nameforHuman || '- -'}
                  transformMenu={true}
                  userPermissions={userPermissions}
                />
              </Grid>
            </RunSparkModalSideBar>
          )}
        </>
      )}
    </Grid>
  );
};

export default SparkPopupHeader;
